import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";

type FeedbackData = any;

function Stars({
  value,
  onChange,
  disabled,
}: {
  value: number;
  onChange: (v: number) => void;
  disabled?: boolean;
}) {
  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: 5 }).map((_, idx) => {
        const v = idx + 1;
        const active = v <= value;
        return (
          <button
            key={v}
            type="button"
            disabled={disabled}
            onClick={() => onChange(v)}
            className="p-1 rounded-md focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-60"
            aria-label={`Rate ${v} star${v === 1 ? "" : "s"}`}
          >
            <Star className={`h-7 w-7 ${active ? "text-yellow-500" : "text-muted-foreground"}`} fill={active ? "currentColor" : "none"} />
          </button>
        );
      })}
    </div>
  );
}

export default function ServiceFeedbackPage() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<FeedbackData | null>(null);
  const [satisfied, setSatisfied] = useState<boolean | null>(null);
  const [rating, setRating] = useState(0);
  const [notes, setNotes] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const business = useMemo(() => (feedback as any)?.business || null, [feedback]);
  const clientPhotosUrl = useMemo(() => {
    const raw = String((business as any)?.client_photos_url || "").trim();
    return /^https?:\/\//i.test(raw) ? raw : "";
  }, [business]);

  useEffect(() => {
    const run = async () => {
      if (!token) {
        setError("No feedback token provided");
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const { data, error: invokeError } = await supabase.functions.invoke("service-feedback", {
          body: { action: "get", token },
        });

        if (invokeError) {
          console.error("Service feedback fetch error:", invokeError);
          const msg = String(invokeError.message || "Failed to load feedback");
          const is404 = msg.includes("404") || (invokeError as any)?.context?.status === 404;
          setError(
            is404
              ? "This feedback link is invalid or has expired. If you just completed a service, try using the link from your latest completion email."
              : msg
          );
          setFeedback(null);
          setLoading(false);
          return;
        }

        if (data?.ok && data?.feedback) {
          setFeedback(data.feedback);
          const existingSatisfied = (data.feedback as any)?.satisfied;
          setSatisfied(existingSatisfied === null || existingSatisfied === undefined ? null : Boolean(existingSatisfied));
          const existingRating = Number(data.feedback?.rating || 0);
          setRating(existingRating);
          setNotes(String((data.feedback as any)?.notes || ""));
          setSubmitted(Boolean(data.feedback?.submitted_at));
        } else if (data?.error) {
          const linkError = String(data.error);
          setError(
            linkError === "Feedback link not found" || linkError.includes("not found")
              ? "This feedback link is invalid or has expired. Please use the link from your most recent completion email."
              : linkError
          );
          setFeedback(null);
        } else {
          console.warn("Service feedback response:", data);
          setError("Feedback link not found or unavailable.");
          setFeedback(null);
        }
      } catch (err) {
        console.error("Service feedback exception:", err);
        setError(err instanceof Error ? err.message : "An unexpected error occurred");
        setFeedback(null);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [token]);

  const handleSubmit = async () => {
    if (!token) return;
    if (satisfied === null || rating < 1) return;
    setSubmitting(true);
    const { data, error } = await supabase.functions.invoke("service-feedback", {
      body: { action: "submit", token, satisfied, rating, notes: notes || null },
    });
    setSubmitting(false);
    if (!error && data?.ok) {
      setSubmitted(true);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!feedback) {
    return (
      <div className="min-h-screen bg-background p-6 flex items-center justify-center">
        <div className="max-w-md w-full text-center">
          <p className="text-sm text-muted-foreground">{error || "Feedback link not found or unavailable."}</p>
          {error ? (
            <p className="mt-2 text-xs text-muted-foreground">Please check the link or contact support if this issue persists.</p>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-6">
          {business?.logo_url ? (
            <img
              src={business.logo_url}
              alt={business?.name || "Business"}
              className="h-14 w-14 rounded-full mx-auto object-cover border border-border"
              loading="lazy"
            />
          ) : null}
          <h1 className="text-2xl font-bold mt-3 text-foreground">{business?.name || "Feedback"}</h1>
          <p className="text-sm text-muted-foreground mt-1">Tell us how your service went.</p>
        </div>

        <Card className="w-full">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Service details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm">
              <div className="flex items-center justify-between gap-3">
                <span className="text-muted-foreground">Service</span>
                <span className="font-medium text-foreground text-right">{(feedback as any)?.service_name || "Service"}</span>
              </div>
              <div className="mt-2 flex items-center justify-between gap-3">
                <span className="text-muted-foreground">Client</span>
                <span className="font-medium text-foreground text-right">{(feedback as any)?.customer_name || "Customer"}</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-sm">Were you satisfied?</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={satisfied === true ? "default" : "outline"}
                  onClick={() => setSatisfied(true)}
                  disabled={submitted || submitting}
                >
                  Yes
                </Button>
                <Button
                  type="button"
                  variant={satisfied === false ? "default" : "outline"}
                  onClick={() => setSatisfied(false)}
                  disabled={submitted || submitting}
                >
                  No
                </Button>
              </div>
              {satisfied === null && !submitted ? <div className="text-xs text-muted-foreground">Pick one option.</div> : null}
            </div>

            <div className="space-y-2">
              <div className="text-sm font-medium text-foreground">Rate the service</div>
              <Stars value={rating} onChange={setRating} disabled={submitted || submitting} />
              {rating < 1 && !submitted ? <div className="text-xs text-muted-foreground">Tap a star to rate.</div> : null}
            </div>

            <div className="space-y-2">
              <div className="text-sm font-medium text-foreground">Notes</div>
              <Textarea
                placeholder="Any notes about how you were served? (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={submitted || submitting}
                className="min-h-[120px]"
              />
            </div>

            {clientPhotosUrl ? (
              <div className="rounded-lg border border-border bg-muted/30 p-3 text-sm">
                <div className="font-medium text-foreground">Your photos</div>
                <p className="text-xs text-muted-foreground mt-1">Download your photos from the link below.</p>
                <a
                  href={clientPhotosUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 inline-block text-sm font-medium text-primary underline underline-offset-4 break-all"
                >
                  {clientPhotosUrl}
                </a>
              </div>
            ) : null}

            {submitted ? (
              <div className="rounded-lg border border-border bg-green-50 dark:bg-green-950/20 p-3 text-sm">
                <div className="font-semibold text-foreground">Thanks!</div>
                <div className="text-muted-foreground mt-1">Your feedback has been submitted.</div>
              </div>
            ) : (
              <Button className="w-full" onClick={handleSubmit} disabled={submitting || satisfied === null || rating < 1}>
                {submitting ? "Submitting..." : "Submit feedback"}
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

